import { useHydratedScoreDetails } from '../hooks/useHydratedScoreDetails';
import type { StoryElement } from '../types';
import { timeDecayAgeLabel } from '../utils/ageBasis';
import { fmt, fmtSigned, fmtDateTime, computeScoreBreakdown } from '../utils/scoreBreakdown';
import { STATUS_COLORS } from '../utils/statusColors';
import { ModalOverlay } from './ModalOverlay';

interface ScoreBreakdownModalProps {
  story: StoryElement;
  onClose: () => void;
}

function Row({
  label,
  value,
  color,
  strong,
  detail,
}: {
  label: React.ReactNode;
  value: string | number;
  color?: string;
  strong?: boolean;
  detail?: boolean;
}) {
  const classNames = [
    strong ? 'score-breakdown-row-strong' : '',
    detail ? 'score-breakdown-row-detail' : '',
  ].filter(Boolean).join(' ') || undefined;
  return (
    <tr className={classNames}>
      <td className="score-breakdown-label">{label}</td>
      <td className="score-breakdown-value" style={color ? { color } : undefined}>{value}</td>
    </tr>
  );
}

function Section({ label }: { label: string }) {
  return (
    <tr className="score-breakdown-section">
      <td colSpan={2}>{label}</td>
    </tr>
  );
}

export function ScoreBreakdownModal({ story: listStory, onClose }: ScoreBreakdownModalProps) {
  const story = useHydratedScoreDetails(listStory);
  const d = story.base_score_details;
  const {
    displayScore,
    clusterRankScore,
    ownScore,
    baseScoreWithoutRelevance,
    relevanceFactor,
    feedScore,
    relevanceScore,
    sourceAdjustment,
    sourceBoost,
    socialAdjustment,
    curatorAdjustment,
    preDecaySubtotal,
    stalePenalty,
    redundantPenalty,
    sourcePenalty,
    otherAdjustmentPenalty,
    sourcePenaltyLabel,
  } = computeScoreBreakdown(story);
  const social = d?.social;
  const socialComponents = social?.components;

  const scrollToSocial = (e: React.MouseEvent) => {
    e.preventDefault();
    const el = document.getElementById('score-breakdown-social-section');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  return (
    <ModalOverlay onClose={onClose} className="score-breakdown-modal">
        <div className="score-breakdown-header">
          <h3 className="score-breakdown-title">Score Breakdown</h3>
          <span className="score-breakdown-score">{displayScore != null ? displayScore.toFixed(2) : '—'}</span>
        </div>

        {clusterRankScore != null && (
          <p className="score-breakdown-cluster-note">
            Ranked by its cluster's strongest article ({clusterRankScore.toFixed(2)}).
            This canonical article's own score is {ownScore != null ? ownScore.toFixed(2) : '—'}.
          </p>
        )}

        {/* An expired score is reset to base_score_details = {} (falsy in Python
            but truthy in JS); treat an empty blob as no breakdown so it shows the
            "not available" note rather than a table of dashes and "Invalid Date". */}
        {d && Object.keys(d).length > 0 ? (
          <>
            <table className="score-breakdown-table">
              <tbody>
                <Section label="Base score" />
                <Row label="Tier (quality)" value={fmt(d.tier)} />
                <Row label="Duplicate boost" value={fmtSigned(d.dupe_boost)} color={d.dupe_boost > 0 ? STATUS_COLORS.success : undefined} />
                <Row label="Topic boost" value={fmtSigned(d.topic_boost)} color={d.topic_boost > 0 ? STATUS_COLORS.success : undefined} />
                <Row label="Source quality boost" value={fmtSigned(sourceBoost)} color={sourceBoost > 0 ? STATUS_COLORS.success : undefined} />
                <Row
                  label={
                    social ? (
                      <a
                        href="#score-breakdown-social-section"
                        onClick={scrollToSocial}
                        className="score-breakdown-jump-link"
                        title="Jump down to detailed social signals breakdown"
                      >
                        {social.category
                          ? `Social adjustment (${social.category}${social.category_multiplier != null ? ` ×${social.category_multiplier}` : ''})`
                          : 'Social adjustment'} <span className="score-breakdown-jump-arrow">↓</span>
                      </a>
                    ) : (
                      'Social adjustment'
                    )
                  }
                  value={fmtSigned(socialAdjustment)}
                  color={socialAdjustment > 0 ? STATUS_COLORS.success : socialAdjustment < 0 ? STATUS_COLORS.error : undefined}
                />
                {curatorAdjustment !== 0 && (
                  <Row
                    label="Curator adjustment"
                    value={fmtSigned(curatorAdjustment)}
                    color={curatorAdjustment > 0 ? STATUS_COLORS.success : STATUS_COLORS.error}
                  />
                )}
                <Row label="Gated penalty" value={d.gated_penalty > 0 ? `-${fmt(d.gated_penalty)}` : '0'} color={d.gated_penalty > 0 ? STATUS_COLORS.error : undefined} />
                <Row label="Adjustment penalties" value={d.adj_penalty > 0 ? `-${fmt(d.adj_penalty)}` : '0'} color={d.adj_penalty > 0 ? STATUS_COLORS.error : undefined} />
                {sourcePenalty > 0 && (
                  <Row label={sourcePenaltyLabel} value={`-${fmt(sourcePenalty)}`} color={STATUS_COLORS.error} detail />
                )}
                {stalePenalty > 0 && (
                  <Row label="Staleness penalty" value={`-${fmt(stalePenalty)}`} color={STATUS_COLORS.error} detail />
                )}
                {redundantPenalty > 0 && (
                  <Row label="Redundancy penalty" value={`-${fmt(redundantPenalty)}`} color={STATUS_COLORS.error} detail />
                )}
                {otherAdjustmentPenalty > 0.0001 && (
                  <Row label="Other adjustment penalty" value={`-${fmt(otherAdjustmentPenalty)}`} color={STATUS_COLORS.error} detail />
                )}
                {preDecaySubtotal != null && (
                  <Row label="Subtotal before age" value={fmt(preDecaySubtotal)} strong />
                )}
                <Row
                  label={`Time decay (${timeDecayAgeLabel(d.age_basis, fmt(d.age_hours, 1))})`}
                  value={`x${fmt(d.time_decay, 4)}`}
                />
                {d.site_floor_applied && d.site_floor != null && (
                  <Row
                    label="Site-quality floor (replaces decayed score)"
                    value={fmt(d.site_floor)}
                    detail
                  />
                )}
                {baseScoreWithoutRelevance != null && (
                  <Row label="Base score" value={fmt(baseScoreWithoutRelevance)} strong />
                )}

                {(relevanceScore != null || relevanceFactor != null || feedScore != null) && (
                  <Section label="Feed-specific score" />
                )}
                {relevanceScore != null && (
                  <Row label="Relevance judgment" value={fmt(relevanceScore, 4)} />
                )}
                {relevanceScore != null && (
                  <Row label="Squared (0.5 = neutral)" value={fmt(relevanceScore * relevanceScore, 4)} />
                )}
                {relevanceFactor != null && (
                  <Row label="Relevance factor" value={`x${fmt(relevanceFactor, 4)}`} />
                )}
                {feedScore != null && (
                  <Row label="Feed score" value={fmt(feedScore)} color={STATUS_COLORS.success} strong />
                )}
              </tbody>
            </table>

            <div className="score-breakdown-details">
              <div className="score-breakdown-detail-row">
                <span>Duplicates</span><span>{d.dupe_count}</span>
              </div>
              <div className="score-breakdown-detail-row">
                <span>Topic siblings</span><span>{d.topic_count}</span>
              </div>
              <div className="score-breakdown-detail-row">
                <span>Canonical (0–5)</span><span>{story.canonical_score != null ? story.canonical_score : '—'}</span>
              </div>
              {(d.source_key || d.source_score != null) && (
                <div className="score-breakdown-detail-row">
                  <span>Source</span><span>{d.source_key ?? '-'}{d.source_score != null ? ` · score ${d.source_score}` : ''}</span>
                </div>
              )}
              {sourceAdjustment !== 0 && (
                <div className="score-breakdown-detail-row">
                  <span>Source quality adjustment</span><span>{fmtSigned(sourceAdjustment)}</span>
                </div>
              )}
              {social && (
                <div id="score-breakdown-social-section" className="score-breakdown-social-block">
                  <div className="score-breakdown-detail-row" style={{ fontWeight: 600, color: 'var(--ink)' }}>
                    <span>Social signals ({social.category || story.category || 'general'}{social.category_multiplier != null ? ` · ×${social.category_multiplier}` : ''})</span>
                    <span>{fmtSigned(social.adjustment ?? social.boost, 4)}</span>
                  </div>
                  <div className="score-breakdown-detail-row">
                    <span>· HN points</span>
                    <span>{social.hn_score}{socialComponents?.hn_score ? ` · ${fmtSigned(socialComponents.hn_score, 4)}` : ''}</span>
                  </div>
                  <div className="score-breakdown-detail-row">
                    <span>· HN comments</span>
                    <span>{social.hn_comments}{socialComponents?.hn_comments ? ` · ${fmtSigned(socialComponents.hn_comments, 4)}` : ''}</span>
                  </div>
                  {(social.lobsters_score != null || social.lobsters_comments != null || socialComponents?.lobsters_score || socialComponents?.lobsters_comments) && (
                    <>
                      <div className="score-breakdown-detail-row">
                        <span>· Lobsters points</span>
                        <span>{social.lobsters_score ?? 0}{socialComponents?.lobsters_score ? ` · ${fmtSigned(socialComponents.lobsters_score, 4)}` : ''}</span>
                      </div>
                      <div className="score-breakdown-detail-row">
                        <span>· Lobsters comments</span>
                        <span>{social.lobsters_comments ?? 0}{socialComponents?.lobsters_comments ? ` · ${fmtSigned(socialComponents.lobsters_comments, 4)}` : ''}</span>
                      </div>
                    </>
                  )}
                  <div className="score-breakdown-detail-row">
                    <span>· GitHub stars</span>
                    <span>{social.github_stars}{socialComponents?.github_stars ? ` · ${fmtSigned(socialComponents.github_stars, 4)}` : ''}</span>
                  </div>
                  <div className="score-breakdown-detail-row">
                    <span>· X likes</span>
                    <span>{social.x_likes}{socialComponents?.x_likes ? ` · ${fmtSigned(socialComponents.x_likes, 4)}` : ''}</span>
                  </div>
                  {(social.alphaxiv_likes != null || socialComponents?.alphaxiv_likes) && (
                    <div className="score-breakdown-detail-row">
                      <span>· alphaXiv likes</span>
                      <span>{social.alphaxiv_likes ?? 0}{socialComponents?.alphaxiv_likes ? ` · ${fmtSigned(socialComponents.alphaxiv_likes, 4)}` : ''}</span>
                    </div>
                  )}
                  {(social.substack_hearts != null || social.substack_comments != null || socialComponents?.substack_hearts || socialComponents?.substack_comments) && (
                    <>
                      <div className="score-breakdown-detail-row">
                        <span>· Substack hearts</span>
                        <span>{social.substack_hearts ?? 0}{socialComponents?.substack_hearts ? ` · ${fmtSigned(socialComponents.substack_hearts, 4)}` : ''}</span>
                      </div>
                      <div className="score-breakdown-detail-row">
                        <span>· Substack comments</span>
                        <span>{social.substack_comments ?? 0}{socialComponents?.substack_comments ? ` · ${fmtSigned(socialComponents.substack_comments, 4)}` : ''}</span>
                      </div>
                    </>
                  )}
                  <div className="score-breakdown-detail-row">
                    <span>· Techmeme</span>
                    <span>{social.techmeme_present ? 'yes' : 'no'}{socialComponents?.techmeme ? ` · ${fmtSigned(socialComponents.techmeme, 4)}` : ''}</span>
                  </div>
                  {socialComponents?.raw_sum != null && (
                    <div className="score-breakdown-detail-row">
                      <span>· Social sum (raw → scaled)</span>
                      <span>{fmtSigned(socialComponents.raw_sum, 4)}{socialComponents.scaled_sum != null && socialComponents.scaled_sum !== socialComponents.raw_sum ? ` → ${fmtSigned(socialComponents.scaled_sum, 4)}` : ''}</span>
                    </div>
                  )}
                  {socialComponents?.raw_sum != null && socialComponents.capped_at != null && Math.abs(socialComponents.scaled_sum ?? socialComponents.raw_sum) > socialComponents.capped_at && (
                    <div className="score-breakdown-detail-row">
                      <span>· Social cap</span><span>±{fmt(socialComponents.capped_at, 4)} (applied)</span>
                    </div>
                  )}
                  {social.floor_penalty != null && social.floor_penalty > 0 && (
                    <div className="score-breakdown-detail-row" style={{ color: STATUS_COLORS.error }}>
                      <span>· HN floor penalty</span><span>-{fmt(social.floor_penalty, 4)}</span>
                    </div>
                  )}
                </div>
              )}
              {Math.abs(d.effective_boost - (d.dupe_boost + d.topic_boost)) > 0.0001 && (
                <div className="score-breakdown-detail-row">
                  <span>Effective boost</span><span>{fmtSigned(d.effective_boost, 4)}</span>
                </div>
              )}
              <div className="score-breakdown-detail-row score-breakdown-computed">
                <span>Computed</span><span>{fmtDateTime(d.when_computed)}</span>
              </div>
            </div>
          </>
        ) : (
          <p className="score-breakdown-empty">No score breakdown available for this article.</p>
        )}

        {story.judgment_justification && (
          <div className="score-breakdown-details">
            <div className="score-breakdown-detail-row score-breakdown-justification">
              <span>Relevance note</span><span>{story.judgment_justification}</span>
            </div>
          </div>
        )}

        {story.llm_relevance_judgments && story.llm_relevance_judgments.length > 0 && (
          <section className="score-breakdown-llm-judgments">
            <h4>LLM relevance judgments</h4>
            {story.llm_relevance_judgments.map((judgment) => (
              <div
                className="score-breakdown-llm-judgment"
                key={`${judgment.cover}-${judgment.judge_model}`}
              >
                <div className="score-breakdown-llm-judgment-header">
                  <strong>{judgment.judge_model}</strong>
                  <span>{judgment.relevant ? 'Relevant' : 'Not relevant'}</span>
                </div>
                {judgment.from_current_run === false && (
                  /* This article was served from the judgment cache, so it has
                     no verdict of its own in this run. Showing the earlier run's
                     verdict is the point -- unlabelled, it read as this run's. */
                  <div className="score-breakdown-llm-judgment-provenance">
                    From an earlier run of this search
                    {judgment.source_search_id ? ` (${judgment.source_search_id.slice(0, 8)})` : ''}
                  </div>
                )}
                <div>
                  Relevance score: {judgment.relevance_score != null
                    ? fmt(judgment.relevance_score, 4)
                    : '—'}
                </div>
                <p>{judgment.justification || 'No justification returned.'}</p>
              </div>
            ))}
          </section>
        )}

        <button className="score-breakdown-close" onClick={onClose}>Close</button>
    </ModalOverlay>
  );
}
